import analyticsService from "../AnalyticsService";
import { isApiTrackingEnabled } from "../constants";

const nowMs = () => globalThis.performance?.now?.() ?? Date.now();

const SAFE_ERROR_MESSAGES = new Set([
  "Bad Request",
  "Unauthorized",
  "Forbidden",
  "Not Found",
  "Method Not Allowed",
  "Request Timeout",
  "Conflict",
  "Gone",
  "Unprocessable Entity",
  "Too Many Requests",
  "Internal Server Error",
  "Not Implemented",
  "Bad Gateway",
  "Service Unavailable",
  "Gateway Timeout",
]);

const getErrorParams = (data) => ({
  ...(typeof data?.error === "string" &&
    /^[A-Z][A-Z_]{0,99}$/.test(data.error) && {
      error_code: data.error,
    }),
  ...(SAFE_ERROR_MESSAGES.has(data?.message) && {
    error_message: data.message,
  }),
});

export const createApiTracker = () => {
  const requestTimes = new Map();
  let requestSequence = 0;

  const onRequest = (config = {}) => {
    if (!isApiTrackingEnabled()) {
      return { metadata: config.metadata };
    }

    const requestId = `api-${++requestSequence}`;
    requestTimes.set(requestId, nowMs());

    return {
      requestId,
      metadata: {
        ...(config.metadata || {}),
        requestId,
      },
    };
  };

  const onResponse = ({
    config,
    status,
    data,
    ok = status >= 200 && status < 400,
  }) => {
    if (!isApiTrackingEnabled()) return;

    const requestId = config?.metadata?.requestId;
    const start = requestTimes.get(requestId);
    const responseTime =
      start === undefined ? undefined : Math.max(0, nowMs() - start);
    requestTimes.delete(requestId);
    const success = ok && data?.status !== "ERROR";

    analyticsService.trackAPI({
      ...(typeof config?.metadata?.apiName === "string" && config.metadata.apiName
        ? { api_name: config.metadata.apiName }
        : {}),
      method: config?.method?.toUpperCase() || "GET",
      ...(responseTime === undefined
        ? {}
        : { response_time: Number(responseTime.toFixed(2)) }),
      status_code: status || 0,
      success,
      ...(!success && getErrorParams(data)),
    });
  };

  return {
    onRequest,
    onResponse,
  };
};

export const attachApiTracker = (client) => {
  if (!isApiTrackingEnabled()) return;

  const tracker = createApiTracker();
  client.interceptors.request.use((config) => {
    config.metadata = tracker.onRequest(config).metadata;
    return config;
  });
  client.interceptors.response.use(
    (response) => {
      tracker.onResponse({
        config: response.config,
        status: response.status,
        data: response.data,
      });
      return response;
    },
    (error) => {
      tracker.onResponse({
        config: error.config,
        status: error.response?.status,
        data: error.response?.data,
        ok: false,
      });
      return Promise.reject(error);
    },
  );
};

export default createApiTracker;
